import { useSessionFeatureState } from '@hooks/storeHooks/useSessionState';
import { useCallback, useEffect, useRef } from 'react';

export interface DataChannelMessage {
  type?: string;
  event?: string;
  data?: unknown;
  [key: string]: any;
}

export const useDataChannel = () => {
  const { dataChannel } = useSessionFeatureState();
  const messageCallbacksRef = useRef(
    new Set<(message: DataChannelMessage) => void>(),
  );

  // Set up message listener when dataChannel becomes available
  useEffect(() => {
    if (!dataChannel) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data) as DataChannelMessage;
        messageCallbacksRef.current.forEach((callback) => {
          try {
            callback(message);
          } catch (callbackError) {
            console.error(
              'Error while executing dataChannel message callback:',
              callbackError,
            );
          }
        });
      } catch (error) {
        console.error('Failed to parse dataChannel message:', error);
      }
    };

    // Use addEventListener to add our listener alongside any existing onmessage handler
    dataChannel.addEventListener('message', handleMessage);

    return () => {
      dataChannel.removeEventListener('message', handleMessage);
    };
  }, [dataChannel]);

  const sendMessage = useCallback(
    (message: DataChannelMessage) => {
      if (!dataChannel) {
        console.warn('DataChannel is not available');
        return;
      }

      if (dataChannel.readyState !== 'open') {
        console.warn(
          `Cannot send message. DataChannel state is: ${dataChannel.readyState}`,
        );
        return;
      }

      try {
        dataChannel.send(JSON.stringify(message));
      } catch (error) {
        console.error('Failed to send message through dataChannel:', error);
      }
    },
    [dataChannel],
  );

  const onMessage = useCallback(
    (callback: (message: DataChannelMessage) => void) => {
      messageCallbacksRef.current.add(callback);
      return () => {
        messageCallbacksRef.current.delete(callback);
      };
    },
    [],
  );

  const isOpen = dataChannel?.readyState === 'open';

  return {
    dataChannel,
    isOpen,
    sendMessage,
    onMessage,
  };
};
